import { Button, Card, Space, message } from "antd";
import { DetailPageErrorState } from "@/views/components/DetailPageErrorState";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    ZhMap
} from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { CDN_URL, DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { useAreaLib } from "@/utils/hooks/useAreaLib";
import { confirmDialog } from "@/utils/confirmDialog";
import { OIL_SITE_STATE } from "@zhihaoscm/meta/src/oilSite";

export interface OilSiteDetailData {
    id: string | number;
    name?: string;
    brand?: string;
    sitePhone?: string;
    phone?: string;
    quantity?: number;
    provinceCode?: string | number;
    cityCode?: string | number;
    regionCode?: string | number;
    address?: string;
    latLon?: string;
    state?: number;
    createdTime?: string;
}

export interface OilSiteDetailPageProps {
    loadDetail?: (id: string) => Promise<OilSiteDetailData>;
    disableSite?: (id: string | number) => Promise<unknown>;
    enableSite?: (id: string | number) => Promise<unknown>;
    deleteSite?: (id: string | number) => Promise<unknown>;
    permissions?: string[];
}

/** 将详情接口的经纬度文本转换为 zhihao-ui-react 地图所需的经纬度元组。 */
function parseOilSiteMapCenter(latLon?: string): [number, number] | undefined {
    const coordinates = latLon?.split(",").map(value => Number(value.trim()));
    if (!coordinates || coordinates.length < 2 || coordinates.some(value => !Number.isFinite(value))) return undefined;
    return [coordinates[0], coordinates[1]];
}

interface OilSiteMapDialogProps {
    detail: OilSiteDetailData;
    open: boolean;
    onClose: () => void;
}

/** 详情页专用查看弹窗，对齐旧 Vue Scan 模式，只读展示当前油站地图位置。 */
function OilSiteMapDialog({ detail, open, onClose }: OilSiteMapDialogProps) {
    return (
        <ZhDialog
            open={open}
            title="查看位置"
            width="920px"
            closeOnClickModal={false}
            destroyOnClose
            showClose
            onCancel={onClose}
        >
            <section aria-label="油站位置地图" className="relative h-[482px] w-full">
                <ZhMap
                    center={parseOilSiteMapCenter(detail.latLon)}
                    layerType="satellite"
                    className="h-full w-full"
                    showScale
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <img alt="油站位置标记" className="h-12" src={`${CDN_URL}map/poi-marker-default.png`} />
                        <span aria-label="地图中心坐标" className="rounded bg-white/90 px-2 py-1 shadow">
                            {detail.latLon || DEFAULT_VALUE}
                        </span>
                    </div>
                </div>
            </section>
        </ZhDialog>
    );
}

/** 复用 Vue 站点详情接口。 */
export async function loadOilSiteDetail(id: string): Promise<OilSiteDetailData> {
    const response = await shippingRequestHttp.service.get<OilSiteDetailData>(`/oil-site/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 站点状态更新接口。 */
export async function updateOilSiteDetailState(id: string | number, state: number) {
    const response = await shippingRequestHttp.service.post(`/oil-site/update/${id}/${state}`);
    return response.data;
}

/** 复用 Vue 站点删除接口。 */
export async function removeOilSite(id: string | number) {
    const response = await shippingRequestHttp.service.delete(`/oil-site/${id}`);
    return response.data;
}

/** 加油站点详情，使用 zhihao-ui-react 承载旧 Vue 的详情字段和状态操作边界。 */
export function OilSiteDetailPage({
    loadDetail = loadOilSiteDetail,
    disableSite = id => updateOilSiteDetailState(id, OIL_SITE_STATE.DISABLE.code),
    enableSite = id => updateOilSiteDetailState(id, OIL_SITE_STATE.ENABLE.code),
    deleteSite = removeOilSite,
    permissions
}: OilSiteDetailPageProps) {
    const areaStore = useAreaLib();
    const location = useLocation();
    const { id: routeId } = useParams();
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue v-auth：站点写权限统一控制修改、启用、禁用和删除入口。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_GAS_STATION_W");
    const [data, setData] = useState<OilSiteDetailData>();
    const [errorMessage, setErrorMessage] = useState("");
    const [mapOpen, setMapOpen] = useState(false);

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setData(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("站点详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const runAction = async (action: () => Promise<unknown>, successText: string, remove = false) => {
        try {
            await action();
            message.success(successText);
            if (remove) navigate(-1);
            else setData(await loadDetail(id));
        } catch {
            setErrorMessage("站点操作失败");
        }
    };

    if (errorMessage)
        return (
            <DetailPageErrorState ariaLabel="oil-site-detail-page" message={errorMessage} onBack={() => navigate(-1)} />
        );
    if (!data) return <section aria-label="oil-site-detail-page" />;

    return (
        <section aria-label="oil-site-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: data.id }]}
                    rightInfos={[{ label: "创建时间", value: data.createdTime || DEFAULT_VALUE }]}
                    stateDesc={Number(data.state) === OIL_SITE_STATE.ENABLE.code ? "启用" : "禁用"}
                    title="站点详情"
                />
                <div className="mt-6">
                    <div className="mb-4 text-base font-medium">基本信息</div>
                    <ZhGrid cols={4} gap={[20, 16]}>
                        <ZhBaseItem label="站点名称" value={data.name || DEFAULT_VALUE} />
                        <ZhBaseItem label="品牌" value={data.brand || DEFAULT_VALUE} />
                        <ZhBaseItem label="站点联系方式" value={data.sitePhone || DEFAULT_VALUE} />
                        <ZhBaseItem label="维护人联系方式" value={data.phone || DEFAULT_VALUE} />
                        <ZhBaseItem label="价格*数量" span={4} value={data.quantity ?? DEFAULT_VALUE} />
                        <ZhBaseItem
                            label="地址"
                            span={2}
                            value={`${areaStore.getFullAreaName(
                                data.provinceCode?.toString(),
                                data.cityCode?.toString(),
                                data.regionCode?.toString()
                            )} ${data.address || DEFAULT_VALUE}`.trim()}
                        />
                        <div className="flex items-center">
                            <ZhBaseItem label="经纬度" value={data.latLon || DEFAULT_VALUE} />
                            <Button
                                aria-label="查看位置"
                                className="ml-1"
                                size="small"
                                type="link"
                                onClick={() => setMapOpen(true)}
                            >
                                查看位置
                            </Button>
                        </div>
                    </ZhGrid>
                </div>
            </Card>
            <Card>
                <Space>
                    {canWrite ? (
                        <Button
                            aria-label="修改"
                            disabled={banButton}
                            onClick={() => navigate(`/logistics/oil/oilSiteEdit/${data.id}`)}
                        >
                            修改
                        </Button>
                    ) : null}
                    {canWrite && Number(data.state) === OIL_SITE_STATE.ENABLE.code ? (
                        <Button
                            aria-label="禁用"
                            disabled={banButton}
                            onClick={() => void runAction(() => disableSite(data.id), "已禁用")}
                        >
                            禁用
                        </Button>
                    ) : null}
                    {canWrite && Number(data.state) !== OIL_SITE_STATE.ENABLE.code ? (
                        <>
                            <Button
                                aria-label="启用"
                                disabled={banButton}
                                onClick={() => void runAction(() => enableSite(data.id), "已启用")}
                            >
                                启用
                            </Button>
                            <Button
                                aria-label="删除"
                                disabled={banButton}
                                onClick={() =>
                                    void (async () => {
                                        if (!(await confirmDialog("是否删除站点？"))) return;
                                        await runAction(() => deleteSite(data.id), "已删除", true);
                                    })()
                                }
                            >
                                删除
                            </Button>
                        </>
                    ) : null}
                    <Button aria-label="返回" disabled={banButton} type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <OilSiteMapDialog detail={data} open={mapOpen} onClose={() => setMapOpen(false)} />
        </section>
    );
}

export default OilSiteDetailPage;
